import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X as XIcon, Copy, ShieldPlus, Sparkles, Search, Package,
  MapPin, Calendar, Tag, FileText,
} from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

/* ---- Disney + Universal resort name list for autocomplete ---- */
const RESORT_SUGGESTIONS = [
  // Disney World — Deluxe
  "Disney's Grand Floridian Resort & Spa",
  "Disney's Polynesian Village Resort",
  "Disney's Contemporary Resort",
  "Disney's Bay Lake Tower",
  "Disney's Animal Kingdom Lodge",
  "Disney's Animal Kingdom Villas – Kidani",
  "Disney's Wilderness Lodge",
  "Disney's Villas at Wilderness Lodge",
  "Disney's BoardWalk Inn",
  "Disney's BoardWalk Villas",
  "Disney's Yacht Club Resort",
  "Disney's Beach Club Resort",
  "Disney's Beach Club Villas",
  // Disney World — Moderate
  "Disney's Coronado Springs Resort",
  "Disney's Caribbean Beach Resort",
  "Disney's Port Orleans Resort – French Quarter",
  "Disney's Port Orleans Resort – Riverside",
  // Disney World — Value
  "Disney's All-Star Movies Resort",
  "Disney's All-Star Music Resort",
  "Disney's All-Star Sports Resort",
  "Disney's Pop Century Resort",
  "Disney's Art of Animation Resort",
  // Disney DVC
  "Disney's Saratoga Springs Resort & Spa",
  "Disney's Old Key West Resort",
  "Disney's Riviera Resort",
  // Disney World — Other on property
  "Shades of Green",
  "Walt Disney World Swan",
  "Walt Disney World Dolphin",
  "Walt Disney World Swan Reserve",
  "Four Seasons Resort Orlando at Walt Disney World",
  "Waldorf Astoria Orlando",
  "Signia by Hilton Orlando Bonnet Creek",
  "Hilton Orlando Bonnet Creek",
  // Universal Orlando
  "Hard Rock Hotel at Universal Orlando",
  "Loews Royal Pacific Resort at Universal",
  "Loews Portofino Bay Hotel at Universal",
  "Loews Sapphire Falls Resort at Universal",
  "Universal's Cabana Bay Beach Resort",
  "Universal's Aventura Hotel",
  "Universal's Endless Summer Resort – Surfside Inn",
  "Universal's Endless Summer Resort – Dockside Inn",
  // Popular off-property areas
  "Reunion Resort",
  "ChampionsGate Resort",
  "Kissimmee (Airbnb / Vacation Home)",
  "Celebration (Airbnb / Vacation Home)",
  "Davenport (Airbnb / Vacation Home)",
  "Marriott's Grande Vista",
  "Marriott's Cypress Harbour",
  "Marriott's Lakeshore Reserve",
  "Hilton Orlando",
  "Gaylord Palms Resort & Convention Center",
  "Rosen Shingle Creek",
  "Omni Orlando Resort at ChampionsGate",
];

/* ---- Resort autocomplete input ---- */
const ResortAutocomplete = ({ value, onChange, placeholder, "data-testid": testId }) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const suggestions = useMemo(() => {
    if (!value || value.length < 2) return [];
    const q = value.toLowerCase();
    return RESORT_SUGGESTIONS.filter((r) => r.toLowerCase().includes(q)).slice(0, 8);
  }, [value]);

  // Close on click outside
  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const showDropdown = open && focused && suggestions.length > 0;

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {showDropdown && (
        <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0E1E2D] border border-[#FFD166]/30 rounded-xl shadow-xl overflow-hidden" data-testid="resort-suggestions">
          {suggestions.map((s) => (
            <li key={s}>
              <button
                type="button"
                onMouseDown={() => { onChange(s); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-[#F6F1E4] hover:bg-[#FFD166]/10 transition-colors flex items-center gap-2"
                data-testid={`resort-suggestion-${s.replace(/\W+/g, "-").toLowerCase()}`}
              >
                <MapPin className="w-3 h-3 text-[#FFD166] shrink-0" />
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ==================== Admin: New Phone/Walk-in Booking ====================
 * Admin enters delivery address, dates, gear, and protection options.
 * Customer info (name, email, phone) is collected from the customer when
 * they open the payment link.
 *
 * Admin controls: address is locked on the customer side.
 * Customer can change: dates, time windows, Care/PoM, add contact info.
 */

const CATEGORY_LABELS = {
  all: "All",
  strollers: "Strollers",
  "baby-gear": "Baby Gear",
  accessories: "Accessories",
  bundles: "Bundles",
};

const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;

/* ----- Live pricing panel ----- */
const PricingPanel = ({ quote, loading, hasItems, hasRequiredFields }) => {
  if (!hasItems || !hasRequiredFields) {
    return (
      <div className="glass p-5 rounded-xl text-center text-[#B9C8D5]" data-testid="nb-pricing-empty">
        <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-xs">Add gear + dates to see pricing</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="glass p-5 rounded-xl text-center text-[#B9C8D5] text-sm animate-pulse" data-testid="nb-pricing-loading">
        Calculating…
      </div>
    );
  }
  if (!quote) return null;

  const rows = [
    { label: "Subtotal", value: quote.subtotal, testid: "nb-q-subtotal" },
    (quote.total_discounts > 0) && { label: "Discounts", value: -quote.total_discounts, cls: "text-emerald-400", testid: "nb-q-discounts" },
    { label: `Delivery (${quote.days}d)`, value: quote.delivery_fee, testid: "nb-q-delivery" },
    (quote.enchanted_care_fee > 0) && { label: "Enchanted Care", value: quote.enchanted_care_fee, testid: "nb-q-care" },
    (quote.enchanted_pom_fee > 0) && { label: "Peace of Mind", value: quote.enchanted_pom_fee, testid: "nb-q-pom" },
    (quote.last_minute_fee > 0) && { label: "Last-minute fee", value: quote.last_minute_fee, cls: "text-[#F4A93A]", testid: "nb-q-lm" },
    { label: `Tax (${((quote.tax_rate || 0) * 100).toFixed(1)}%)`, value: quote.tax, testid: "nb-q-tax" },
  ].filter(Boolean);

  return (
    <div className="glass rounded-xl overflow-hidden" data-testid="nb-pricing-panel">
      <div className="px-4 py-3 border-b border-[#FFD166]/15">
        <p className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Pricing preview</p>
      </div>
      <div className="px-4 py-3 space-y-2">
        {rows.map((r) => (
          <div key={r.testid} className={`flex justify-between text-sm ${r.cls || "text-[#F6F1E4]"}`} data-testid={r.testid}>
            <span>{r.label}</span>
            <span className={r.value < 0 ? "text-emerald-400" : ""}>{r.value < 0 ? `-${fmt(-r.value)}` : fmt(r.value)}</span>
          </div>
        ))}
        <div className="border-t border-[#FFD166]/20 pt-2 flex justify-between items-baseline" data-testid="nb-q-total">
          <span className="font-semibold text-[#F6F1E4]">Total</span>
          <span className="font-serif text-2xl text-[#FFD166]">{fmt(quote.total)}</span>
        </div>
      </div>
      {(quote.line_items || []).length > 0 && (
        <div className="px-4 pb-3 space-y-0.5 border-t border-[#FFD166]/10">
          {quote.line_items.map((li, i) => (
            <div key={i} className="flex justify-between text-xs text-[#B9C8D5]">
              <span>{li.name} ×{li.quantity}</span>
              <span>{fmt(li.subtotal)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ----- Protection toggles ----- */
const ProtectionToggles = ({ quote, enchantedCare, setEnchantedCare, enchantedPom, setEnchantedPom }) => {
  const pomEligible = !!(quote?.pom_eligible);

  return (
    <section data-testid="nb-protection-section">
      <div className="eyebrow mb-2 flex items-center gap-1.5">
        <ShieldPlus className="w-3.5 h-3.5" /> Protection add-ons
        <span className="text-[10px] text-[#B9C8D5] font-normal ml-1">— customer can also change these on their end</span>
      </div>
      <div className="space-y-2">
        <label className={`flex items-start gap-3 cursor-pointer border-2 rounded-xl p-4 transition-colors ${enchantedCare ? "border-emerald-500/50 bg-emerald-500/5" : "border-[#FFD166]/15 bg-black/25"}`} data-testid="nb-care-wrap">
          <input type="checkbox" checked={enchantedCare} onChange={(e) => setEnchantedCare(e.target.checked)} className="mt-1 w-5 h-5 accent-[#FFD166] shrink-0" data-testid="nb-care-check" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <ShieldPlus className="w-4 h-4 text-[#FFD166] shrink-0" />
              <span className="font-medium text-[#F6F1E4]">Enchanted Care</span>
              {quote?.enchanted_care_fee > 0 && <span className="text-[#FFD166] font-serif text-base" data-testid="nb-care-fee">+{fmt(quote.enchanted_care_fee)}</span>}
              <span className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">8% · $5 min</span>
            </div>
            <p className="text-[11px] text-[#B9C8D5] mt-1">Covers accidental damage during the rental. Refundable if cancelled before delivery.</p>
          </div>
        </label>

        {pomEligible ? (
          <label className={`flex items-start gap-3 cursor-pointer border-2 rounded-xl p-4 transition-colors ${enchantedPom ? "border-[#FFD166]/60 bg-[#FFD166]/5" : "border-[#FFD166]/15 bg-black/25"}`} data-testid="nb-pom-wrap">
            <input type="checkbox" checked={enchantedPom} onChange={(e) => setEnchantedPom(e.target.checked)} className="mt-1 w-5 h-5 accent-[#FFD166] shrink-0" data-testid="nb-pom-check" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFD166] shrink-0" />
                <span className="font-medium text-[#F6F1E4]">Peace of Mind</span>
                {quote?.enchanted_pom_fee > 0 && <span className="text-[#FFD166] font-serif text-base" data-testid="nb-pom-fee">+{fmt(quote.enchanted_pom_fee)}</span>}
                <span className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">20% · cancel anytime</span>
              </div>
              <p className="text-[11px] text-[#B9C8D5] mt-1">Cancel before delivery for a full rental refund. PoM fee non-refundable.</p>
            </div>
          </label>
        ) : (
          <div className="flex items-start gap-3 border-2 border-dashed border-[#FFD166]/15 rounded-xl p-4 bg-black/20 text-[11px] text-[#B9C8D5]" data-testid="nb-pom-locked">
            <Sparkles className="w-4 h-4 text-[#B9C8D5] shrink-0 mt-0.5" />
            <span><span className="text-[#F6F1E4]">Peace of Mind</span> — available when booking 7+ days before delivery</span>
          </div>
        )}
      </div>
    </section>
  );
};

/* ----- Gear browser with tabs + search ----- */
const GearBrowser = ({ products, cart, setCart }) => {
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const categories = useMemo(() => {
    const cats = new Set(products.map((p) => p.is_bundle ? "bundles" : p.category).filter(Boolean));
    return ["all", ...Array.from(cats).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.active !== false && p.website_visible !== false);
    if (activeTab !== "all") {
      list = list.filter((p) => (p.is_bundle ? "bundles" : p.category) === activeTab);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list;
  }, [products, activeTab, searchText]);

  const addItem = (id, delta) => {
    setCart((c) => {
      const next = { ...c, [id]: Math.max(0, (c[id] || 0) + delta) };
      if (next[id] === 0) delete next[id];
      return next;
    });
  };

  const selectedCount = Object.values(cart).reduce((s, v) => s + v, 0);

  return (
    <section data-testid="nb-gear-section">
      <div className="eyebrow mb-2 flex items-center gap-1.5">
        <Package className="w-3.5 h-3.5" /> Gear
        {selectedCount > 0 && <span className="ml-auto text-[#FFD166] text-xs font-normal">{selectedCount} item{selectedCount !== 1 ? "s" : ""} selected</span>}
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#B9C8D5]" />
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search gear…"
          className="w-full bg-black/30 border border-[#FFD166]/20 text-[#F6F1E4] rounded-lg pl-9 pr-3 py-2 text-sm focus:border-[#FFD166]/50 focus:outline-none"
          data-testid="nb-gear-search"
        />
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 scrollbar-none" data-testid="nb-gear-tabs">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveTab(cat)}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeTab === cat ? "bg-[#FFD166] text-[#0A2438]" : "bg-black/30 border border-[#FFD166]/20 text-[#B9C8D5] hover:border-[#FFD166]/50"}`}
            data-testid={`nb-tab-${cat}`}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      <div className="max-h-[280px] overflow-y-auto space-y-1 border border-[#FFD166]/10 rounded-xl p-2 bg-black/20" data-testid="nb-gear-list">
        {filtered.length === 0 && (
          <p className="text-center text-[#B9C8D5] text-xs py-6">No items match</p>
        )}
        {filtered.map((p) => {
          const qty = cart[p.id] || 0;
          return (
            <div key={p.id} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${qty > 0 ? "bg-[#FFD166]/8 border border-[#FFD166]/20" : "hover:bg-black/30"}`} data-testid={`nb-product-${p.id}`}>
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name} className="w-8 h-8 rounded object-cover shrink-0 opacity-80" />
              ) : (
                <div className="w-8 h-8 rounded bg-[#FFD166]/10 shrink-0 flex items-center justify-center">
                  <Package className="w-4 h-4 text-[#FFD166]/40" />
                </div>
              )}
              <span className="flex-1 text-sm text-[#F6F1E4] min-w-0 truncate">
                {p.name}
                {p.is_bundle && <span className="ml-1 text-[10px] bg-[#FFD166]/15 text-[#FFD166] rounded px-1">Bundle</span>}
              </span>
              <span className="text-xs text-[#B9C8D5] shrink-0">${p.price_per_day}/d</span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" onClick={() => addItem(p.id, -1)} disabled={qty === 0} className="w-6 h-6 rounded-full border border-[#FFD166]/30 text-[#FFD166] disabled:opacity-25 hover:border-[#FFD166] text-base leading-none" data-testid={`nb-dec-${p.id}`}>−</button>
                <span className="w-5 text-center text-sm font-medium">{qty}</span>
                <button type="button" onClick={() => addItem(p.id, +1)} className="w-6 h-6 rounded-full border border-[#FFD166]/30 text-[#FFD166] hover:border-[#FFD166] text-base leading-none" data-testid={`nb-inc-${p.id}`}>+</button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ============================= Main Modal ============================= */
export const NewBookingModal = ({ onClose, onDone }) => {
  const [products, setProducts] = useState([]);
  const [zones, setZones] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Delivery
  const [zoneId, setZoneId] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");

  // Dates
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliverySlot, setDeliverySlot] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupSlot, setPickupSlot] = useState("");

  // Gear
  const [cart, setCart] = useState({});

  // Protection
  const [enchantedCare, setEnchantedCare] = useState(false);
  const [enchantedPom, setEnchantedPom] = useState(false);

  // Other
  const [promoCode, setPromoCode] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  // Live quote
  const [liveQuote, setLiveQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const quoteTimerRef = useRef(null);

  // Step 2
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // ---- Load data ----
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/zones`),
      axios.get(`${API}/time-slots`),
    ]).then(([p, z, t]) => {
      setProducts(p.data.products || []);
      setZones(z.data.zones || []);
      setTimeSlots(t.data.slots || []);
      if ((z.data.zones || []).length > 0) setZoneId(z.data.zones[0].id);
    });
  }, []);

  // ---- Live quote ----
  const items = useMemo(() =>
    Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity })),
  [cart]);

  const hasRequiredFields = !!(deliveryDate && pickupDate && deliverySlot && pickupSlot && zoneId);

  useEffect(() => {
    if (!hasRequiredFields || items.length === 0) { setLiveQuote(null); return; }
    if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current);
    quoteTimerRef.current = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const { data } = await axios.post(`${API}/quote`, {
          start_date: deliveryDate,
          end_date: pickupDate,
          items,
          zone_id: zoneId,
          enchanted_care: enchantedCare,
          enchanted_pom: enchantedPom,
          promo_code: promoCode.trim().toUpperCase() || null,
        });
        setLiveQuote(data);
      } catch (_) { setLiveQuote(null); }
      finally { setQuoteLoading(false); }
    }, 500);
    return () => clearTimeout(quoteTimerRef.current);
  }, [items, deliveryDate, pickupDate, deliverySlot, pickupSlot, zoneId, enchantedCare, enchantedPom, promoCode, hasRequiredFields]);

  // ---- Validation ----
  const canCreate = zoneId && deliveryDate && pickupDate && deliverySlot && pickupSlot && items.length > 0;

  // ---- Create booking ----
  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        zone_id: zoneId,
        delivery_resort: deliveryLocation.trim(),
        delivery_date: deliveryDate,
        delivery_slot: deliverySlot,
        pickup_date: pickupDate,
        pickup_slot: pickupSlot,
        items,
        enchanted_care: enchantedCare,
        enchanted_pom: enchantedPom,
        promo_code: promoCode.trim().toUpperCase() || null,
        notes: adminNotes.trim(),
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/admin/bookings/create-manual`, payload);
      setResult(data);
      setStep(2);
      toast.success("Booking created — share the payment link with the customer.");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---- Copy link ----
  const copyLink = () => {
    if (!result?.resume_url) return;
    navigator.clipboard?.writeText(result.resume_url);
    setCopied(true);
    toast.success("Payment link copied!");
    setTimeout(() => setCopied(false), 3000);
  };

  const goToCheckout = () => {
    if (!result?.resume_url) return;
    window.open(result.resume_url, "_blank", "noopener");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="new-booking-modal"
    >
      <div className="max-w-5xl w-full my-6 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 z-10 text-[#FFD166] hover:text-white" data-testid="new-booking-close">
          <XIcon className="w-5 h-5" />
        </button>

        <div className="glass p-6 sm:p-8">
          <h2 className="font-serif text-3xl text-[#FFD166] mb-1">New booking</h2>
          <p className="text-sm text-[#B9C8D5] mb-6">
            Set the delivery address, gear, and dates. The customer enters their name, email, and phone when they open the payment link.
          </p>

          {/* ---- Step 1: Form ---- */}
          {step === 1 && (
            <div className="grid lg:grid-cols-[1fr_300px] gap-6 lg:gap-8" data-testid="new-booking-step-form">

              {/* Left: Form */}
              <div className="space-y-6 min-w-0">

                {/* Delivery destination */}
                <section>
                  <div className="eyebrow mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Delivery destination</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 block">Pricing zone *</label>
                      <select
                        value={zoneId}
                        onChange={(e) => setZoneId(e.target.value)}
                        className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm"
                        data-testid="nb-zone"
                      >
                        {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 block">Address / resort</label>
                      <ResortAutocomplete
                        value={deliveryLocation}
                        onChange={setDeliveryLocation}
                        placeholder="e.g. Disney's Grand Floridian or 123 Oak St"
                        data-testid="nb-delivery-location"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#B9C8D5] mt-1.5">
                    Address is locked on the customer's checkout — only you can change it.
                  </p>
                </section>

                {/* Dates & slots */}
                <section>
                  <div className="eyebrow mb-2 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Dates &amp; time windows</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 block">Delivery date *</label>
                      <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="nb-deliv-date" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 block">Delivery window *</label>
                      <select value={deliverySlot} onChange={(e) => setDeliverySlot(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="nb-deliv-slot">
                        <option value="">Choose…</option>
                        {timeSlots.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 block">Pickup date *</label>
                      <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} data-testid="nb-pickup-date" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 block">Pickup window *</label>
                      <select value={pickupSlot} onChange={(e) => setPickupSlot(e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="nb-pickup-slot">
                        <option value="">Choose…</option>
                        {timeSlots.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#B9C8D5] mt-1.5">
                    Customer can change dates and time windows on their end.
                  </p>
                </section>

                {/* Gear browser */}
                <GearBrowser products={products} cart={cart} setCart={setCart} />

                {/* Protection */}
                <ProtectionToggles
                  quote={liveQuote}
                  enchantedCare={enchantedCare}
                  setEnchantedCare={setEnchantedCare}
                  enchantedPom={enchantedPom}
                  setEnchantedPom={setEnchantedPom}
                />

                {/* Promo + notes */}
                <section className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 flex items-center gap-1 block"><Tag className="w-3 h-3" /> Promo code (optional)</label>
                    <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="WELCOME10" className="tracking-wider" data-testid="nb-promo" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-[#B9C8D5] mb-1 flex items-center gap-1 block"><FileText className="w-3 h-3" /> Internal notes (admin only)</label>
                    <Input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Called at 3pm, needs early delivery" data-testid="nb-notes" />
                  </div>
                </section>

                <div className="flex justify-end gap-2 pt-2 border-t border-[#FFD166]/10">
                  <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
                  <Btn onClick={create} disabled={!canCreate || busy} data-testid="nb-create-btn">
                    {busy ? "Creating…" : "Create booking →"}
                  </Btn>
                </div>
              </div>

              {/* Right: Live pricing */}
              <div className="lg:sticky lg:top-6 space-y-4 self-start">
                <PricingPanel
                  quote={liveQuote}
                  loading={quoteLoading}
                  hasItems={items.length > 0}
                  hasRequiredFields={hasRequiredFields}
                />
                {liveQuote && (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200/80">
                    Customer fills their name, email &amp; phone on the payment link. They can also adjust Care &amp; PoM.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ---- Step 2: Payment link ---- */}
          {step === 2 && result && (
            <div className="max-w-lg" data-testid="new-booking-step-pay">

              {/* Booking summary */}
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 mb-6">
                <div className="text-sm text-emerald-300 mb-1">Booking created</div>
                <div className="font-serif text-3xl text-[#FFD166] mb-1">{fmt(result.quote.total)}</div>
                <div className="text-[11px] text-[#B9C8D5] font-mono mb-2">{result.reservation_id}</div>
                <div className="text-xs text-[#B9C8D5] space-y-0.5">
                  {result.quote.enchanted_care_fee > 0 && <div>Enchanted Care included (+{fmt(result.quote.enchanted_care_fee)})</div>}
                  {result.quote.enchanted_pom_fee > 0 && <div>Peace of Mind included (+{fmt(result.quote.enchanted_pom_fee)})</div>}
                </div>
              </div>

              {/* Share the link */}
              <div className="rounded-xl border border-[#FFD166]/20 bg-black/30 p-5 mb-4">
                <div className="eyebrow mb-2">Customer payment link</div>
                <p className="text-xs text-[#B9C8D5] mb-3">
                  Share this link with the customer however you like — WhatsApp, SMS, email. They'll enter their contact info, can adjust dates &amp; times, add protection, and pay.
                </p>
                <div className="flex gap-2 items-center mb-3">
                  <div className="flex-1 text-[10px] font-mono bg-black/50 border border-[#FFD166]/20 rounded-lg px-3 py-2 truncate text-[#B9C8D5]" data-testid="nb-link-preview">
                    {result.resume_url}
                  </div>
                  <Btn onClick={copyLink} data-testid="nb-copy-link">
                    <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy"}
                  </Btn>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Here is your Enchanted Gear Rentals payment link:\n${result.resume_url}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#25D366] text-white hover:bg-[#1ebe5d] transition-colors"
                    data-testid="nb-whatsapp-btn"
                  >
                    {/* WhatsApp icon SVG */}
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Share via WhatsApp
                  </a>
                  <Btn variant="ghost" onClick={goToCheckout} className="text-xs" data-testid="nb-open-checkout">
                    Preview checkout in new tab
                  </Btn>
                </div>
              </div>

              <div className="rounded-xl border border-[#FFD166]/10 bg-black/20 p-4 text-xs text-[#B9C8D5]">
                <span className="text-[#FFD166] font-medium">What the customer sees:</span> The delivery address is shown but cannot be changed. They enter their name, email, and phone. They can adjust delivery and pickup dates if availability allows. They choose Care &amp; Protection, agree to terms, and pay.
              </div>

              <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[#FFD166]/10">
                <Btn onClick={() => onDone()} data-testid="nb-done-btn">Done</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewBookingModal;
